import { MonetizationType, OfferHistory } from '@/@types/graphql-types';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { computed } from 'vue';
import { useI18n } from '@/helpers/composables/useI18n';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';

export type SlimOfferHistory = Omit<OfferHistory, 'package'> & {
	package: Partial<OfferHistory['package']>;
};

export const useStreamingOffersHistory = ({
	offersHistory,
	titleName,
}: {
	offersHistory: MaybeRefOrGetter<SlimOfferHistory[]>;
	titleName?: MaybeRefOrGetter<string>;
}) => {
	const { country, language } = storeToRefs(useLanguageStore());

	const { t, tc } = useI18n();

	const dateFormatter = new Intl.DateTimeFormat(`${toValue(language).split('-')[0]}-${toValue(country)}`, {
		month: 'long',
		year: 'numeric',
	});

	const spinningText = computed(() => {
		const title = toValue(titleName);
		const countryName = tc(`WEBAPP_COUNTRY_${toValue(country)}`);
		const historyData = toValue(offersHistory);
		const totalOffers = historyData.length;

		if (totalOffers === 0) return '';

		// Single pass: get offers with latest end dates and sort
		const offersWithDates = historyData.map(offer => {
			const latestEndTime = Math.max(...offer.dateRanges.map(range => new Date(range.end).getTime()));
			return {
				offer,
				latestEndDate: new Date(latestEndTime),
				latestEndTime,
			};
		});

		// Sort once by latest end date (most recent first)
		offersWithDates.sort((a, b) => b.latestEndTime - a.latestEndTime);

		const mostRecentDate = offersWithDates[0].latestEndDate;
		const mostRecentYear = mostRecentDate.getFullYear();
		const mostRecentMonth = mostRecentDate.getMonth();

		// Single pass: filter recent offers and collect provider names
		const providerNames: string[] = [];
		const recentProviderNames: string[] = [];

		for (const { offer, latestEndDate } of offersWithDates) {
			const clearName = offer.package.clearName ?? '';
			providerNames.push(clearName);

			if (latestEndDate.getFullYear() === mostRecentYear && latestEndDate.getMonth() === mostRecentMonth) {
				recentProviderNames.push(clearName);
			}
		}

		// Format the end date of the most recent offer
		const mostRecentOffer = offersWithDates[0].offer;
		const finalEndDate = mostRecentOffer.dateRanges.reduce((latest, range) => {
			const endTime = new Date(range.end).getTime();
			return endTime > new Date(latest).getTime() ? range.end : latest;
		}, mostRecentOffer.dateRanges[0].end);
		const formattedEndDate = dateFormatter.format(new Date(finalEndDate));

		const primaryProvider = providerNames[0] ?? '';
		const secondaryProvider = providerNames[1] ?? '';

		if (totalOffers === 1) {
			return t('WEBAPP_STREAMING_OFFER_HISTORY_SPINNING_TEXT_1_PREV_OFFER', {
				title: title,
				country: countryName,
				date: formattedEndDate,
				provider: primaryProvider,
			});
		}

		if (totalOffers === 2) {
			return t('WEBAPP_STREAMING_OFFER_HISTORY_SPINNING_2_PREV_OFFERS', {
				title: title,
				country: countryName,
				date: formattedEndDate,
				firstProvider: primaryProvider,
				secondProvider: secondaryProvider,
				lastProvider: recentProviderNames[0] ?? '',
			});
		}

		// Multiple offers (3 or more)
		if (recentProviderNames.length <= 4) {
			return t('WEBAPP_STREAMING_OFFER_HISTORY_SPINNING_TEXT_MULTIPLE_PREV_OFFERS_UPTO4LASTPROVIDER', {
				title: title,
				country: countryName,
				total: totalOffers,
				serviceList: recentProviderNames.join(', '),
				date: formattedEndDate,
			});
		}

		return t('WEBAPP_STREAMING_OFFER_HISTORY_SPINNING_TEXT_MULTIPLE_PREV_OFFERS_4ANDMORELASTPROVIDER', {
			title: title,
			country: countryName,
			date: formattedEndDate,
			firstProvider: recentProviderNames[0] ?? '',
			X: recentProviderNames.length - 1,
		});
	});

	const sortedOffersHistory = computed(() => {
		const historyData = toValue(offersHistory);

		// Single pass: calculate max end dates and filter
		const filtered = historyData.filter(item => !item.package.monetizationTypes?.includes(MonetizationType.Cinema));

		// Map with end time for sorting (avoid recalculating)
		const withEndTimes = filtered.map(item => {
			const maxEnd = Math.max(...item.dateRanges.map(el => new Date(el.end).getTime()));
			return { item, maxEnd };
		});

		// Sort by pre-calculated end times
		withEndTimes.sort((a, b) => b.maxEnd - a.maxEnd);

		return withEndTimes.map(({ item }) => item);
	});

	return {
		dateFormatter,
		spinningText,
		sortedOffersHistory,
	};
};
